<template>
    <div class="is-relative">
        <transition
            enter-active-class="animated slideInUp"
            leave-active-class="animated fadeOut"
            mode="out-in"
        >
            <slot></slot>
        </transition>

        <div class="flex flex-col items-center border rounded-sm min-h-full">
            <div class="lb-clean-input w-full">
                <div class="has-background-light" @click.stop>
                    <button-input-rich-text
                        :editor="editor"
                        :type="'bold'"
                        @click="editor.chain().focus().toggleBold().run()"
                    />
                    <button-input-rich-text
                        :editor="editor"
                        :type="'italic'"
                        @click="editor.chain().focus().toggleItalic().run()"
                    />
                    <button-input-rich-text
                        :editor="editor"
                        :type="'bulletList'"
                        @click="editor.chain().focus().toggleBulletList().run()"
                    />
                    <button-input-rich-text
                        :editor="editor"
                        :type="'underline'"
                        @click="editor.chain().focus().toggleUnderline().run()"
                    />
                    <button
                        class="button is-text is-inverted"
                        @click="sendImage"
                    >
                        <input
                            class="file-input"
                            type="file"
                            accept="image/*"
                            multiple="true"
                            @change="sendImage"
                        />
                        <span class="icon is-medium">
                            <i class="mdi mdi-18px mdi-camera"></i>
                        </span>
                    </button>
                </div>
            </div>
            <div class="flex w-full">
                <editor-content
                    :editor="editor"
                    class="editor__content w-full"
                />
                <div>
                    <button
                        :disabled="!message.length"
                        class="has-text-primary h-full has-background-pale-grey disabled:cursor-not-allowed disabled:opacity-75"
                        @click="sendMessage"
                    >
                        <span class="icon is-medium">
                            <i class="mdi mdi-18px mdi-send"></i>
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppMentionsDropdown from '@/components/AppMentionsDropdown';
import { Editor, EditorContent, BubbleMenu, VueRenderer } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import ButtonInputRichText from '@/components/ButtonInputRichText';
import Underline from '@tiptap/extension-underline';
import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import { upload } from '@/services/API';

export default {
    components: {
        EditorContent,
        ButtonInputRichText,
    },
    props: {
        preMessage: {
            type: Object,
            default: function () {
                return {};
            },
        },
        suggestions: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    data() {
        return {
            message: this.preMessage.message ? this.preMessage.message : '',
            showUserDropdown: false,
            loading: true,
            regex: new RegExp('@[0-9a-z]*', 'g'),
            height: 'auto',
            editor: new Editor({
                autofocus: true,
                content: this.message,
                extensions: [
                    StarterKit,
                    Underline,
                    Mention.configure({
                        HTMLAttributes: {
                            class: 'mention',
                        },
                        suggestion: {
                            items: (query) => {
                                return this.suggestions
                                    .filter((item) =>
                                        item.username
                                            .toLowerCase()
                                            .startsWith(query.toLowerCase())
                                    )
                                    .slice(0, 5);
                            },
                            render: () => {
                                if (!this.suggestions.length) {
                                    return;
                                }
                                let component;
                                let popup;

                                return {
                                    onStart: (props) => {
                                        component = new VueRenderer(
                                            AppMentionsDropdown,
                                            {
                                                parent: this,
                                                propsData: {
                                                    ...props,
                                                    ...{
                                                        loading: false,
                                                        renderUp: true,
                                                    },
                                                },
                                            }
                                        );

                                        popup = tippy('body', {
                                            getReferenceClientRect:
                                                props.clientRect,
                                            appendTo: () => document.body,
                                            content: component.element,
                                            showOnCreate: true,
                                            interactive: true,
                                            trigger: 'manual',
                                            placement: 'bottom-start',
                                        });
                                    },
                                    onUpdate(props) {
                                        component.updateProps(props);
                                        popup[0].setProps({
                                            getReferenceClientRect:
                                                props.clientRect,
                                        });
                                    },
                                    onKeyDown(props) {
                                        if (component.ref)
                                            return component.ref.onKeyDown(
                                                props
                                            );
                                    },
                                    onExit() {
                                        popup[0].destroy();
                                        component.destroy();
                                    },
                                };
                            },
                        },
                    }),
                    Placeholder.configure({
                        showOnlyCurrent: false,
                        emptyEditorClass: 'editor-empty',
                        placeholder: () => {
                            if (!this.placeholder) {
                                return this.$t(
                                    'Object.comments_input_placeholder'
                                );
                            }
                            return this.placeholder;
                        },
                    }),
                ],
                onUpdate: this.updateText,
            }),
        };
    },
    computed: {},
    watch: {
        preMessage() {
            this.message = this.preMessage.message
                ? this.preMessage.message
                : '';
            this.editor.commands.setContent(this.message);
        },
        message() {
            if (!this.message.length) {
                this.showUserDropdown = false;
            }
        },
        users() {
            if (!this.users.length) {
                this.showUserDropdown = false;
            }
        },
    },
    mounted() {},
    methods: {
        updateText() {
            this.message = this.editor.getHTML();
        },
        async sendImage({ target }) {
            if (target.files.length == 0) {
                return;
            }
            let response = await upload(`files`, target.files).catch((err) => {
                return this.$emit('error', err);
            });
            if (response.data) {
                response.data.files.forEach((file) => {
                    this.$emit('comment', {
                        message: this.message,
                        media: file,
                    });
                    this.message = ''; //only send text with the very first image
                });
                //this.files = [...this.files, ...response.data.files];
                //this.$emit("input", this.files);
            }
        },
        sendMessage() {
            if (!this.message.length) {
                return;
            }
            if (!this.preMessage.message) {
                this.$emit('comment', { message: this.message });
            } else {
                this.$emit('updateComment', this.preMessage, this.message);
            }
            this.message = '';
            this.height = 'auto';
            this.editor.commands.setContent();
        },
    },
};
</script>

<style scoped>
.lb-message-input {
    position: relative;
    bottom: 0;
}

.lb-grow {
    min-height: 2rem;
    max-height: 8rem;
    border-color: transparent;
}
.editor__content {
    max-height: 10rem;
    overflow-y: scroll;
}
</style>

<style lang="scss" scoped>
::v-deep {
    /* Basic editor styles */
    .ProseMirror {
        min-height: 80px;
        max-height: 10rem;
        padding: 1rem;
        > * + * {
            margin-top: 0.75em;
        }
        ul,
        ol {
            padding: 0 1rem;
        }
        code {
            background-color: rgba(#616161, 0.1);
            color: #616161;
        }
        pre {
            background: #0d0d0d;
            color: #fff;
            font-family: 'JetBrainsMono', monospace;
            padding: 0.75rem 1rem;
            border-radius: 0.5rem;
            code {
                color: inherit;
                padding: 0;
                background: none;
                font-size: 0.8rem;
            }
        }
        img {
            max-width: 100%;
            height: auto;
        }
        blockquote {
            padding-left: 1rem;
            border-left: 2px solid rgba(#0d0d0d, 0.1);
        }
        hr {
            border: none;
            border-top: 2px solid rgba(#0d0d0d, 0.1);
            margin: 2rem 0;
        }
    }

    .ProseMirror:focus {
        outline: none;
    }

    /* Placeholder (at the top) */
    .ProseMirror p.is-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: #ced4da;
        pointer-events: none;
        height: 0;
    }

    /* Placeholder (on every new line) */
    /*.ProseMirror p.is-empty::before {
    content: attr(data-placeholder);
    float: left;
    color: #ced4da;
    pointer-events: none;
    height: 0;
  }*/

    a {
        color: #68cef8;
    }
}
</style>
